import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import {
  fetchAdgroupsForNegativeModal,
  fetchCampaignsForNegativeModal,
  NEGATIVE_LEVEL,
  NEGATIVE_MATCH_TYPE,
  mapMatchTypeToAdjustmentType,
} from "../../helper/bulkSelection/bulkSearchTermNegativeUpdate";

const VISIBLE_TAG_LIMIT = 2;

function StatusDot({ status }) {
  const normalized = String(status ?? "").toUpperCase();
  if (!normalized) return null;
  const isEnabled = normalized === "ENABLED";
  return (
    <Box
      title={normalized}
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: isEnabled ? "#12B76A" : "#98A2B3",
        alignSelf: "center",
      }}
    />
  );
}

function renderLimitedTags(value, getTagProps, getLabel) {
  const visible = value.slice(0, VISIBLE_TAG_LIMIT);
  const hiddenCount = value.length - visible.length;

  return (
    <>
      {visible.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return (
          <Chip
            key={key}
            size="small"
            label={getLabel(option)}
            {...tagProps}
            sx={{ maxWidth: 140 }}
          />
        );
      })}
      {hiddenCount > 0 ? (
        <Chip
          size="small"
          label={`+${hiddenCount}`}
          sx={{
            fontWeight: 700,
            bgcolor: "#F3EFFF",
            color: "#6B4EFF",
          }}
        />
      ) : null}
    </>
  );
}
const LEVEL_OPTIONS = [
  {
    key: NEGATIVE_LEVEL.RESPECTIVE,
    label: "Respective Campaign & Ad Group",
    hint: "Add on each selected search term's own campaign/ad group",
    Icon: AccountTreeRoundedIcon,
    accent: "#6B4EFF",
    accentBg: "#F3EFFF",
  },
  {
    key: NEGATIVE_LEVEL.ACCOUNT,
    label: "Account Level",
    hint: "Add as account-level negative targeting",
    Icon: BusinessRoundedIcon,
    accent: "#2E90FA",
    accentBg: "#EFF8FF",
  },
  {
    key: NEGATIVE_LEVEL.CAMPAIGN,
    label: "Campaign Level",
    hint: "Applicable on selected Campaign's AdGroups",
    Icon: CampaignRoundedIcon,
    accent: "#12B76A",
    accentBg: "#ECFDF3",
  },
  {
    key: NEGATIVE_LEVEL.ADGROUP,
    label: "Ad Group Level",
    hint: "Choose campaigns, then ad groups",
    Icon: GroupsRoundedIcon,
    accent: "#F79009",
    accentBg: "#FFFAEB",
  },
];

const MATCH_OPTIONS = [
  { key: "Exact", value: NEGATIVE_MATCH_TYPE.EXACT, label: "Exact" },
  { key: "Phrase", value: NEGATIVE_MATCH_TYPE.PHRASE, label: "Phrase" },
];

/**
 * Attractive MUI modal for Search Term V2 → Add To Negative.
 */
export default function AddToNegativeModalV2({
  open = false,
  clientId,
  showMatchType = true,
  forceExactMatchType = false,
  infoNote = null,
  onClose,
  onSubmit,
}) {
  const [level, setLevel] = useState("");
  const [matchType, setMatchType] = useState("Exact");
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [adgroupOptions, setAdgroupOptions] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedAdgroups, setSelectedAdgroups] = useState([]);
  const [campaignInput, setCampaignInput] = useState("");
  const [adgroupInput, setAdgroupInput] = useState("");
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingAdgroups, setIsLoadingAdgroups] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLevel("");
    setMatchType("Exact");
    setSelectedCampaigns([]);
    setSelectedAdgroups([]);
    setCampaignOptions([]);
    setAdgroupOptions([]);
    setCampaignInput("");
    setAdgroupInput("");
  }, [open]);

  useEffect(() => {
    if (!open || !clientId) return;
    if (level !== NEGATIVE_LEVEL.CAMPAIGN && level !== NEGATIVE_LEVEL.ADGROUP) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsLoadingCampaigns(true);
      try {
        const rows = await fetchCampaignsForNegativeModal({
          clientId,
          searchTerm: campaignInput,
          limit: 200,
        });
        if (!cancelled) setCampaignOptions(rows);
      } catch (error) {
        console.error(error);
        if (!cancelled) setCampaignOptions([]);
      } finally {
        if (!cancelled) setIsLoadingCampaigns(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, clientId, level, campaignInput]);

  useEffect(() => {
    if (!open || !clientId) return;
    if (level !== NEGATIVE_LEVEL.ADGROUP) return;
    if (!selectedCampaigns.length) {
      setAdgroupOptions([]);
      setSelectedAdgroups([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsLoadingAdgroups(true);
      try {
        const campaignAlphaIds = selectedCampaigns.map((c) => c.id).filter(Boolean);
        const rows = await fetchAdgroupsForNegativeModal({
          clientId,
          campaignAlphaIds,
          searchTerm: adgroupInput,
          limit: 500,
        });
        if (!cancelled) {
          setAdgroupOptions(rows);
          setSelectedAdgroups((prev) =>
            prev.filter((ag) => rows.some((row) => row.value === ag.value))
          );
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) setAdgroupOptions([]);
      } finally {
        if (!cancelled) setIsLoadingAdgroups(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, clientId, level, selectedCampaigns, adgroupInput]);

  const hideMatchType = forceExactMatchType || !showMatchType;

  const isSubmitDisabled = useMemo(() => {
    if (!level) return true;
    if (level === NEGATIVE_LEVEL.CAMPAIGN && !selectedCampaigns.length) return true;
    if (level === NEGATIVE_LEVEL.ADGROUP && !selectedAdgroups.length) return true;
    if (!hideMatchType && !matchType) return true;
    return false;
  }, [level, selectedCampaigns, selectedAdgroups, hideMatchType, matchType]);

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    const adjustmentType = mapMatchTypeToAdjustmentType(matchType, {
      forceExact: hideMatchType,
    });
    onSubmit?.({
      level,
      adjustmentType,
      matchType: hideMatchType ? "Exact" : matchType,
      campaignPlatformIds: selectedCampaigns.map((c) => c.platform_id || c.value).filter(Boolean),
      adsetPlatformIds: selectedAdgroups.map((a) => a.platform_id || a.value).filter(Boolean),
      label: LEVEL_OPTIONS.find((o) => o.key === level)?.label || level,
    });
  };

  const needsCampaignDropdown =
    level === NEGATIVE_LEVEL.CAMPAIGN || level === NEGATIVE_LEVEL.ADGROUP;
  const needsAdgroupDropdown = level === NEGATIVE_LEVEL.ADGROUP;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 16px 48px rgba(16, 24, 40, 0.18)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          mb: 1.5,
          background: "linear-gradient(135deg, #F7F5FF 0%, #FFFFFF 55%)",
          borderBottom: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111" }}>
              Add To Negative Targeting
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}>
              Choose level, match type, and destination campaigns/ad groups.
            </Typography>
            {infoNote ? (
              <Typography sx={{ fontSize: "11px", color: "error.main", mt: 0.75 }}>
                * {infoNote}
              </Typography>
            ) : null}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 2.5 }}>
        <Stack spacing={2.25}>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#333", mb: 1 }}>
              Negative targeting level
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.25,
              }}
            >
              {LEVEL_OPTIONS.map((option) => {
                const selected = level === option.key;
                const Icon = option.Icon;
                return (
                  <Box
                    key={option.key}
                    onClick={() => {
                      setLevel(option.key);
                      if (option.key !== NEGATIVE_LEVEL.CAMPAIGN && option.key !== NEGATIVE_LEVEL.ADGROUP) {
                        setSelectedCampaigns([]);
                        setSelectedAdgroups([]);
                      }
                      if (option.key === NEGATIVE_LEVEL.CAMPAIGN) {
                        setSelectedAdgroups([]);
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: selected ? option.accent : "grey.200",
                      borderRadius: 2,
                      px: 1.5,
                      py: 1.25,
                      bgcolor: selected ? option.accentBg : "#fff",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        borderColor: option.accent,
                        bgcolor: option.accentBg,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Radio
                        checked={selected}
                        size="small"
                        sx={{
                          p: 0.25,
                          color: option.accent,
                          "&.Mui-checked": { color: option.accent },
                        }}
                      />
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "8px",
                          bgcolor: option.accentBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 16, color: option.accent }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 650, color: "#111" }}>
                          {option.label}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary", mt: 0.25 }}>
                          {option.hint}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {!hideMatchType ? (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
                bgcolor: "#FAFAFB",
                px: 2,
                py: 1.5,
              }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#333", mb: 1 }}>
                Match type
              </Typography>
              <Stack direction="row" spacing={2}>
                {MATCH_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.key}
                    control={
                      <Radio
                        checked={matchType === option.key}
                        onChange={() => setMatchType(option.key)}
                        size="small"
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                        {option.label}
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            </Box>
          ) : null}

          {needsCampaignDropdown ? (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={campaignOptions}
              loading={isLoadingCampaigns}
              value={selectedCampaigns}
              inputValue={campaignInput}
              onInputChange={(_, value) => setCampaignInput(value)}
              onChange={(_, value) => {
                setSelectedCampaigns(value);
                if (level === NEGATIVE_LEVEL.CAMPAIGN) setSelectedAdgroups([]);
              }}
              getOptionLabel={(option) => option?.label || option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderTags={(value, getTagProps) =>
                renderLimitedTags(
                  value,
                  getTagProps,
                  (option) => option?.label || option?.name || ""
                )
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <StatusDot status={option.status} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "12px", lineHeight: 1.3 }} noWrap>
                        {option.label}
                      </Typography>
                      <Typography sx={{ fontSize: "10px", color: "text.secondary", lineHeight: 1.3 }} noWrap>
                        ID: {option.platform_id || option.value}
                      </Typography>
                    </Box>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Campaigns"
                  placeholder="Search campaigns"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingCampaigns ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : null}

          {needsAdgroupDropdown ? (
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={!selectedCampaigns.length}
              options={adgroupOptions}
              loading={isLoadingAdgroups}
              value={selectedAdgroups}
              inputValue={adgroupInput}
              onInputChange={(_, value) => setAdgroupInput(value)}
              onChange={(_, value) => setSelectedAdgroups(value)}
              getOptionLabel={(option) => option?.label || option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderTags={(value, getTagProps) =>
                renderLimitedTags(
                  value,
                  getTagProps,
                  (option) => option?.label || option?.name || ""
                )
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <StatusDot status={option.status} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "12px", lineHeight: 1.3 }} noWrap>
                        {option.label}
                      </Typography>
                      <Typography sx={{ fontSize: "10px", color: "text.secondary", lineHeight: 1.3 }} noWrap>
                        ID: {option.platform_id || option.value}
                      </Typography>
                    </Box>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Ad Groups"
                  placeholder={
                    selectedCampaigns.length
                      ? "Search ad groups"
                      : "Select campaigns first"
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingAdgroups ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            borderColor: "grey.400",
            color: "#333",
            minWidth: 96,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitDisabled}
          sx={{
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            minWidth: 140,
            boxShadow: "none",
            bgcolor: "#6B4EFF",
            "&:hover": { bgcolor: "#5A3FE0", boxShadow: "none" },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
